import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges
} from '@angular/core';

@Directive({
  selector: '[appStatusStyleChanges]',
  standalone: false
})
export class StatusFieldChangesDirective implements AfterViewInit {
  @Input() textTransform: 'uppercase' | 'none' = 'none';
  @Input() boldText: boolean = false;

  // VPDW-3111 rebrand: status colors aligned to brand tokens.
  // See variables.less :root for the source of truth.
  viveGreen: string = '#3EC984'; // brand Verified Green / success
  viveOrange: string = '#F59E0B'; // brand warning
  viveRed: string = '#F44336'; // brand error
  viveGray: string = '#4F5C65'; // brand text-secondary
  viveDarkBlue: string = '#042346'; // brand Deep Navy

  greenStatuses = new Set<string>([
    'ACCEPTED',
    'ACTIVE',
    'ACTIVATED',
    'PASS',
    'PASSING',
    'YES',
    'APPROVED',
    'PASSED',
    'BASIC',
    'FULL',
    'EXEMPT - PASSED',
    'NO LICENSED TRADES',
    'WAIVED FINDINGS',
    'LINKED',
    // VPDW-3111 — additional ViveStatus values (vendor.viveStatus enum)
    // categorized as "good standing". `SITE CONDITIONAL` is an approved
    // state (vendor cleared for specific sites), not a warning.
    'COMPLIANCE EXEMPT',
    'BUSINESS VERIFIED',
    'FORCE ACTIVE',
    'SITE CONDITIONAL'
  ]);

  orangeStatuses = new Set<string>([
    'REGISTERED',
    'MISSING',
    'REVIEWING',
    'REVIEW NEEDED',
    'NO EXACT MATCH',
    'UNDER REVIEW',
    'EXEMPT',
    // VPDW-3111 — ViveStatus values that mean "action needed / transitional".
    'PENDING',
    'WATCH LIST',
    'RESOLVING',
    'AWAITING PAYMENT',
    'NON-VERIFIED'
  ]);

  redStatuses = new Set<string>([
    'FAILED',
    'FAIL',
    'REJECTED',
    'NO',
    'REQUIRED',
    'SUSPENDED',
    'EXPIRED',
    'DEACTIVATED',
    'EXEMPT - FAILED',
    'LICENSE ISSUES',
    // VPDW-3111 — ViveStatus values that mean "blocking / failed".
    'NON-COMPLIANT',
    'DECLINED'
  ]);

  // VPDW-3111 — `RENEWING` belongs here (info / in-progress) rather than
  // warning; it's not a problem state, it's a workflow phase like
  // REGISTERING.
  blueStatuses = new Set<string>(['REGISTERING', 'REGISTERED', 'RENEWING']);

  // Treat "NOT REQUIRED" as muted/secondary — it's a do-nothing state,
  // shouldn't compete visually with MISSING/PASSED/FAILED. "UNLINKED" is
  // the same shape: neutral state, not a problem to fix.
  // VPDW-3111 — `UNREGISTERED` and `INACTIVE` are dormant states, not
  // failures; route to neutral so they read as "no action" instead of
  // alarming red.
  mutedStatuses = new Set<string>([
    'NOT REQUIRED',
    'N/A',
    'UNLINKED',
    'UNREGISTERED',
    'INACTIVE'
  ]);

  allStatuses = new Set<string>([
    ...this.greenStatuses,
    ...this.orangeStatuses,
    ...this.redStatuses,
    ...this.blueStatuses,
    ...this.mutedStatuses
  ]);

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {}

  private isValidStatus(input: string): boolean {
    return this.allStatuses.has(input.trim().toUpperCase());
  }

  private checkStatusColor(status: string): string | '' {
    const formattedStatus = status.trim().toUpperCase();

    if (this.greenStatuses.has(formattedStatus)) {
      return this.viveGreen;
    }

    if (this.orangeStatuses.has(formattedStatus)) {
      return this.viveOrange;
    }

    if (this.redStatuses.has(formattedStatus)) {
      return this.viveRed;
    }

    if (this.blueStatuses.has(formattedStatus)) {
      return this.viveDarkBlue;
    }

    if (this.mutedStatuses.has(formattedStatus)) {
      return this.viveGray;
    }

    return this.viveGray;
  }

  // DESIGN.md: status values render as `.chip-*` pills, not raw colored
  // text. Map the status family → chip class name so this directive can
  // attach the chip styling alongside its existing color/transform behavior.
  private chipClassFor(status: string): string | null {
    const formatted = status.trim().toUpperCase();
    if (this.greenStatuses.has(formatted)) return 'chip chip-success';
    if (this.orangeStatuses.has(formatted)) return 'chip chip-warning';
    if (this.redStatuses.has(formatted)) return 'chip chip-error';
    if (this.blueStatuses.has(formatted)) return 'chip chip-info';
    if (this.mutedStatuses.has(formatted)) return 'chip chip-neutral';
    return null;
  }

  private allChipClasses: string[] = [
    'chip',
    'chip-success',
    'chip-warning',
    'chip-error',
    'chip-info',
    'chip-neutral'
  ];

  // DESIGN.md: chip text is rendered in Title Case, never UPPERCASE. The
  // source data for status fields is mixed (PASSED, Passed, passed, etc.)
  // so normalize once at the chip boundary instead of relying on every
  // template binding to do it.
  private toChipDisplayCase(input: string): string {
    return input
      .trim()
      .toLowerCase()
      .replace(/(^|[\s\/-])([a-z])/g, (_, sep, c) => sep + c.toUpperCase());
  }

  private updateStatusStyles() {
    let status: string = this.el.nativeElement.innerText;
    const validStatus = this.isValidStatus(status);

    if (status) {
      if (this.textTransform === 'uppercase' && validStatus) {
        status = status.toUpperCase();
        this.renderer.setProperty(this.el.nativeElement, 'innerText', status);
      }

      if (this.boldText && validStatus) {
        this.renderer.addClass(this.el.nativeElement, 'fw-semibold');
      }

      // Clear any prior chip classes so a directive bound to a value that
      // mutates (e.g. PASSED → FAILED) doesn't accumulate stale variants.
      this.allChipClasses.forEach((cls) =>
        this.renderer.removeClass(this.el.nativeElement, cls)
      );

      const chipClass = validStatus ? this.chipClassFor(status) : null;
      if (chipClass) {
        // Normalize displayed text to Title Case for any valid status
        // — never UPPERCASE on a chip, per DESIGN.md. Guard the write so
        // the MutationObserver doesn't trigger a recursion loop.
        const display = this.toChipDisplayCase(status);
        if (this.el.nativeElement.innerText !== display) {
          this.renderer.setProperty(
            this.el.nativeElement,
            'innerText',
            display
          );
        }

        chipClass
          .split(' ')
          .forEach((cls) => this.renderer.addClass(this.el.nativeElement, cls));
        // Chip classes carry their own color via .chip-*. Strip the inline
        // color set on earlier passes so the chip palette wins.
        this.renderer.removeStyle(this.el.nativeElement, 'color');
      } else {
        this.renderer.setStyle(
          this.el.nativeElement,
          'color',
          this.checkStatusColor(status)
        );
      }
    }
  }

  ngAfterViewInit(): void {
    this.updateStatusStyles();

    const statusObserver = new MutationObserver(() => {
      this.updateStatusStyles();
    });

    statusObserver.observe(this.el.nativeElement, {
      subtree: true,
      characterData: true,
      childList: true
    });
  }
}
